<template>
	<div class="integration-configuration">
		<div class="step-container">
			<div class="step-header">
				{{ t('common_step_number', { Step: 1 }) }} -
				{{
					t('admin_integrations_connect_to_platform', {
						platform: 'Google Workspace',
					})
				}}
			</div>
			<div class="step-content">
				<Button
					v-if="!connection"
					primary
					@click="connectToGoogleWorkspace"
					:loading="isConnecting"
					>{{
						t('admin_integrations_connect_to_platform', { platform: 'Google' })
					}}</Button
				>
				<Button v-else primary @click="gotoConnection">{{
					t('common_refresh')
				}}</Button>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref } from 'vue'
import { Button, useAlert } from '@/components/common'
import { integrationPost } from '@/api/integrations'
import store from '@/store'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getEnv } from '@/helperMethods'
// Props
const props = defineProps({
	integrationId: { type: [String, Number], required: true },
})

// Utilities
const { t } = useI18n()
const alert = useAlert()

// Reactive State
const connection = ref(null)
const isConnecting = ref(false)
const router = useRouter()

// Methods
const connectToGoogleWorkspace = async () => {
	isConnecting.value = true
	try {
		connection.value = await integrationPost(
			`/${props.integrationId}/connections`
		)
	} catch (e) {
		console.error(e)
		// The service localizes its own errors off the Accept-Language header the
		// integrations client sends, so surface its message rather than mapping
		// codes here (e.g. MAX_INTEGRATION_INSTALLS when the org is at its limit)
		alert.error(e?.response?.data?.message || t('apierrors_error_generic'))
		isConnecting.value = false
		return
	}

	// isConnecting deliberately stays true past here: the OAuth tab takes over

	// once we have a connection, we redirect the user to the oauth configuration we did not want to pass the JWT token in the URL
	// so we create a hidden form and submit it to the oauth configuration
	const form = document.createElement('form')
	form.method = 'POST'
	const subdomain = ['dev', 'local'].includes(getEnv())
		? 'integrations.dev'
		: 'integrations'
	form.action = `https://${subdomain}.pronto.io/google_workspace/oauth/connect/${connection.value.id}`
	form.target = '_blank'
	form.style.display = 'none'

	const tokenInput = document.createElement('input')
	tokenInput.type = 'hidden'
	tokenInput.name = 'token' // or whatever field name they expect
	tokenInput.value = store.auth.integration_jwt.token

	form.appendChild(tokenInput)
	document.body.appendChild(form)

	form.submit()
}

const gotoConnection = async () => {
	router.push({ name: 'connection', params: { id: connection.value.id } })
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.integration-configuration {
	width: 100%;
	.step-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		border: solid 1px var(--neutral-200);
		border-radius: 8px;
		width: 100%;
		margin-bottom: 25px;

		.step-header {
			display: flex;
			flex-direction: row;
			align-items: center;
			background-color: #f9f9f9;
			border-bottom: solid 1px var(--neutral-200);
			padding: 19px 22px 11px 22px;
			font-family: 'Avenir-Heavy';
			font-size: 20px;
			color: var(--neutral-800);
			width: 100%;
			border-top-left-radius: 8px;
			border-top-right-radius: 8px;
		}

		.step-content {
			width: 100%;
			padding: 23px 22px 11px 22px;
			.description {
				font-family: 'Avenir-Roman';
				font-size: 16px;
				color: var(--neutral-600);
				margin-bottom: 20px;
			}
			.platform-dropdown {
				width: 325px;
			}
			.documentation-iframe {
				width: 100%;
				height: 100%;
				border: solid 1px var(--neutral-200);
				border-radius: 8px;
				height: 450px;
				margin-bottom: 11px;
				iframe {
					border-radius: 8px;
					width: 100%;
					height: 100%;
				}
			}
		}
	}
}
</style>
